/**
 * A lista de telefones de um contato, dentro do formulário.
 *
 * Papel: desenha uma linha por número — o toggle entre celular e fixo, o campo
 * mascarado e o nome de quem atende —, mais o botão que acrescenta outra linha
 * e o que remove uma.
 *
 * Como usar: o componente não tem estado próprio e precisa do `control` do
 * formulário que o contém, porque é o `useFieldArray` daquele formulário que
 * guarda as linhas. Montá-lo com um `control` de outro formulário faz as linhas
 * aparecerem e nunca chegarem ao envio.
 *
 * A ordem das linhas é a ordem dos números: a primeira é a que a lista de
 * contatos mostra no cartão. Por isso remover a primeira promove a segunda, e
 * não há uma marcação de "principal" que pudesse discordar do que está na tela.
 *
 * O toggle decide quantos dígitos o número aceita, e não apenas o rótulo: fixo
 * exige DDD e mais oito, celular aceita nove e também oito, que é a forma dos
 * celulares anteriores a 2016 ainda presentes na agenda. Trocar o toggle de um
 * celular de nove dígitos para fixo faz `novoContatoMailingSchema` recusar o
 * envio, com a mensagem no campo do número.
 */
import { type Control, Controller, type FieldErrors, useFieldArray } from 'react-hook-form';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import { type ContatoNoFormulario, TELEFONE_EM_BRANCO } from '../types';

interface CamposTelefoneProps {
  control: Control<ContatoNoFormulario>;
  /** Os erros do campo `telefones`, como o formulário os expõe. */
  erros: FieldErrors<ContatoNoFormulario>['telefones'];
  /** Desliga os campos enquanto o formulário envia. */
  desabilitado: boolean;
}

/**
 * Os campos de telefone, um grupo por número.
 *
 * @param control - O `control` do formulário que contém esta lista.
 * @param erros - O ramo `telefones` de `formState.errors`; a mensagem de cada
 *   linha é procurada pela posição dela.
 * @param desabilitado - Verdadeiro enquanto a gravação não responde, o que
 *   também impede acrescentar e remover linhas.
 * @returns O bloco de telefones com o botão de acrescentar.
 */
export function CamposTelefone({ control, erros, desabilitado }: CamposTelefoneProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'telefones' });

  /**
   * A mensagem de erro de uma linha, qualquer que seja o campo que a causou.
   *
   * @param indice - Posição da linha na lista.
   * @returns A primeira mensagem daquela linha, ou `undefined` quando não há erro.
   */
  function erroDaLinha(indice: number): string | undefined {
    const erro = erros?.[indice];
    return erro?.numero?.message ?? erro?.tipo?.message ?? erro?.falarCom?.message;
  }

  /** Acrescenta uma linha em branco ao fim da lista. */
  function handleAcrescentar() {
    append({ ...TELEFONE_EM_BRANCO });
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
        Telefones
      </legend>

      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nenhum telefone. O contato pode ser salvo assim e ganhar números depois.
        </p>
      )}

      {fields.map((campo, indice) => (
        <div key={campo.id} className="rounded-md border border-border bg-card p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`telefone-tipo-${String(indice)}`}>Tipo</Label>
              <Controller
                control={control}
                name={`telefones.${indice}.tipo` as const}
                render={({ field }) => (
                  <ToggleGroup
                    id={`telefone-tipo-${String(indice)}`}
                    type="single"
                    variant="outline"
                    size="sm"
                    value={field.value}
                    disabled={desabilitado}
                    // Um `ToggleGroup` de seleção única devolve string vazia ao
                    // desmarcar o item ativo. Sem a guarda, clicar duas vezes no
                    // tipo já escolhido deixaria o campo sem valor nenhum e o
                    // envio seria recusado num campo que a pessoa não mexeu.
                    onValueChange={(valor) => {
                      if (valor !== '') field.onChange(valor);
                    }}
                  >
                    <ToggleGroupItem value="celular">Celular</ToggleGroupItem>
                    <ToggleGroupItem value="fixo">Fixo</ToggleGroupItem>
                  </ToggleGroup>
                )}
              />
            </div>

            <div className="flex min-w-[10rem] flex-1 flex-col gap-1.5">
              <Label htmlFor={`telefone-numero-${String(indice)}`}>Número</Label>
              <Controller
                control={control}
                name={`telefones.${indice}.numero` as const}
                render={({ field }) => (
                  <CampoMascarado
                    id={`telefone-numero-${String(indice)}`}
                    formato="telefone"
                    placeholder="(11) 98765-4321"
                    disabled={desabilitado}
                    autoComplete="off"
                    {...field}
                  />
                )}
              />
            </div>

            <div className="flex min-w-[10rem] flex-1 flex-col gap-1.5">
              <Label htmlFor={`telefone-falar-com-${String(indice)}`}>Falar com</Label>
              <Controller
                control={control}
                name={`telefones.${indice}.falarCom` as const}
                render={({ field }) => (
                  <Input
                    id={`telefone-falar-com-${String(indice)}`}
                    placeholder="SAC, vendedor, proprietário…"
                    disabled={desabilitado}
                    autoComplete="off"
                    {...field}
                    // O campo esvaziado guarda `null` depois de uma ida à API, e
                    // um `input` com `value` nulo passa a ser não controlado no
                    // meio da digitação, o que trava o campo até o próximo reset.
                    value={field.value ?? ''}
                  />
                )}
              />
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={desabilitado}
              onClick={() => remove(indice)}
            >
              Remover
            </Button>
          </div>

          {erroDaLinha(indice) !== undefined && (
            <p className="mt-2 text-sm text-destructive" role="alert">
              {erroDaLinha(indice)}
            </p>
          )}
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={desabilitado}
          onClick={handleAcrescentar}
        >
          + Outro telefone
        </Button>
      </div>
    </fieldset>
  );
}
